/**
 * An event bus with a Vue instance like API
 * - $on
 * - $off
 * - $once
 * - $emit
 */

type EventHandler = (...args: any[]) => any;

type EventHandlers = {
  [key: string | symbol]: EventHandler[];
};

type IframeHandlers = {
  [key: string]: EventHandler[];
};

type IframeInstance = {
  iframeName: string;
  $on: (type: string | symbol, handler: EventHandler) => void;
  $offAll: () => void;
  $off: (type: string | symbol, handler?: EventHandler) => void;
  $emit: (type: string | symbol, ...args: any[]) => void;
  $once: (type: string | symbol, handler: EventHandler) => void;
  $offByIframe: (iframeName: string) => void;
};

export default class DpEventBus {
  private $_all: EventHandlers;
  private $_iframes: IframeHandlers;

  constructor() {
    this.$_all = {};
    this.$_iframes = {};
  }

  /**
   * Register an event handler for the given type.
   *
   * @param {string|symbol} type Type of event to listen for
   * @param {Function} handler Function to call in response to given event
   * @param {string} iframeName Name of iframe to register component to
   */
  $on(type: string | symbol, handler: EventHandler, iframeName?: string): void {
    const handlers = this.$_all[type];
    const added = handlers && handlers.push(handler);

    if (!added) {
      this.$_all[type] = [handler];
    }

    if (iframeName) {
      this.$_iframes[iframeName] = this.$_iframes[iframeName] || [];
      this.$_iframes[iframeName].push(handler);
    }
  }

  $offByIframe(iframeName: string): void {
    if (!this.$_iframes[iframeName]) {
      return;
    }

    const iframeHandlers = this.$_iframes[iframeName];
    if (!iframeHandlers) {
      return;
    }

    Object.keys(this.$_all).forEach(type => {
      const handlers = this.$_all[type];
      if (!handlers) {
        return;
      }

      const newHandlers = handlers.filter(x => {
        return !iframeHandlers.includes(x);
      });

      if (newHandlers.length) {
        this.$_all[type] = newHandlers;
      } else {
        delete this.$_all[type];
      }
    });

    delete this.$_iframes[iframeName];
  }

  /**
   * Remove all handlers
   */
  $offAll(): void {
    this.$_iframes = {};
    this.$_all = {};
    return;
  }

  /**
   * Remove an event handler or all handlers for the given type.
   *
   * @param {string|symbol} type Type of event to unregister `handler`
   * @param {Function} handler Handler function to remove
   * @param {string} iframeName Name of iframe to register component to
   */
  $off(
    type: string | symbol,
    handler?: EventHandler,
    iframeName?: string
  ): void {
    const handlers = this.$_all[type] || [];

    if (iframeName && this.$_iframes[iframeName]) {
      const iframeHandlers = this.$_iframes[iframeName];
      // filter out one handler
      const newIframeHandlers = handler
        ? iframeHandlers.filter(x => x !== handler)
        : iframeHandlers.filter(x => !handlers.includes(x));

      if (newIframeHandlers.length) {
        this.$_iframes[iframeName] = newIframeHandlers;
      } else {
        delete this.$_iframes[iframeName];
      }
    }

    const newHandlers = handler ? handlers.filter(x => x !== handler) : [];

    if (newHandlers.length) {
      this.$_all[type] = newHandlers;
    } else {
      delete this.$_all[type];
    }
  }

  /**
   * Add an event listener to type but only trigger it once
   *
   * @param {string|symbol} type Type of event to listen for
   * @param {Function} handler Handler function to call in response to event
   * @param {string} iframeName Name of iframe to register component to
   */
  $once(
    type: string | symbol,
    handler: EventHandler,
    iframeName?: string
  ): void {
    const wrapHandler = (...args: any[]): void => {
      this.$off(type, wrapHandler, iframeName);
      handler(...args);
    };
    this.$on(type, wrapHandler, iframeName);
  }

  /**
   * Invoke all handlers for the given type.
   *
   * @param {string|symbol} type The event type to invoke
   * @param {Any} [evt] Any value passed to each handler
   */
  $emit(type: string | symbol, ...args: any[]): void {
    const handlers = this.$_all[type] || [];

    handlers.forEach(handler => {
      handler(...args);
    });
  }

  iframeInstance(iframeName: string): IframeInstance {
    const self = this;
    function $on(type: string | symbol, handler: EventHandler): void {
      self.$on(type, handler, iframeName);
    }

    function $off(type: string | symbol, handler?: EventHandler) {
      self.$off(type, handler, iframeName);
    }

    function $offAll() {
      self.$offAll();
    }

    function $emit(type: string | symbol, ...args: any[]): void {
      self.$emit(type, ...args);
    }

    function $offByIframe(iframeName: string): void {
      self.$offByIframe(iframeName);
    }

    function $once(type: string | symbol, handler: EventHandler) {
      self.$once(type, handler, iframeName);
    }

    return {
      iframeName,
      $on,
      $offAll,
      $off,
      $emit,
      $once,
      $offByIframe,
    };
  }
}
